import React, { useState, useRef, useEffect } from "react";
import { Link } from 'react-router-dom';
import Header from "./Header";
import Footer from "./Footer";
import "./CoverOptions.css";
import { Helmet } from "react-helmet";


<Helmet>
  <title>Plans & Pricing | Affordable Insurance Coverage Options| Kora Insurance</title>
  <meta name="description" content="Explore insurance pricing and flexible coverage options tailored to your needs. Get the best value with Kora." />
  <meta name="keywords" content="Insurance Ghana, Family Insurance, Affordable Cover, Kora Insurance" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://korame.com.gh/" />
  {/* Open Graph tags */}
  <meta property="og:title" content="Best Online Insurance Platform" />
  <meta property="og:description" content="Compare and buy insurance plans with ease." />
  <meta property="og:url" content="https://korame.com.gh/" />
  <meta property="og:type" content="website" />
</Helmet>

const relationships = ["Spouse", "Parent", "Inlaw", "Others"];

const relationshipLimits = {
  "Spouse": 1,
  "Parent": 2,
  "Inlaw": 1,
  "Others": 2,
};


const mainLifeCover = [
  {
    type: "Bronze",
    features: [
      { title: "Life Cover", value: "Gh₵ 1,500" },
      { title: "Disability (Accident)", value: "GH₵ 3,000" },
      { title: "Hospitalization Benefit", value: "GH₵ 50/day" },
      { title: "Monthly Premium", value: "GH₵ 4.32" },
      { title: "Annual Premium", value: "GH₵ 51.84" },
    ],
    monthly: 4.32,
    annual: 51.84,
  },
  {
    type: "Silver",
    features: [
      { title: "Life Cover", value: "Gh₵ 2,500" },
      { title: "Disability (Accident)", value: "GH₵ 5,000" },
      { title: "Hospitalization Benefit", value: "GH₵ 100/day" },
      { title: "Monthly Premium", value: "GH₵ 8.07" },
      { title: "Annual Premium", value: "GH₵ 96.79" },
    ],
    monthly: 8.07,
    annual: 96.79,
  },
  {
    type: "Gold",
    features: [
      { title: "Life Cover", value: "Gh₵ 5,000" },
      { title: "Disability (Accident)", value: "GH₵ 10,000" },
      { title: "Hospitalization Benefit", value: "GH₵ 200/day" },
      { title: "Monthly Premium", value: "GH₵ 16.13" },
      { title: "Annual Premium", value: "GH₵ 193.58" },
    ],
    monthly: 16.13,
    annual: 193.58,
  },
];

const tier1Data = [
  { ageBandLabel: "1-69 years", monthly: 2.63, annual: 31.58 },
  { ageBandLabel: "70-74 years", monthly: 13.25, annual: 159.02 },
  { ageBandLabel: "75-84 years", monthly: 24.18, annual: 290.16 },
];

const tier2Data = [
  { ageBandLabel: "1-69 years", monthly: 5.26, annual: 63.15 },
  { ageBandLabel: "70-74 years", monthly: 26.50, annual: 318.03 },
  { ageBandLabel: "75-84 years", monthly: 48.36, annual: 580.32 },
];

const CoverOptions = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [progressStep, setProgressStep] = useState(1);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const tierSectionRef = useRef(null);
  const ageSectionRef = useRef(null);
  const relationshipSectionRef = useRef(null);
  const coverOptionsRef = useRef(null);
  const beneficiaryRef = useRef(null);
  const summaryRightRef = useRef(null);
  // const [isFixed, setIsFixed] = useState(false);
  // const [summaryTop, setSummaryTop] = useState("auto");

  const planObj = mainLifeCover.find(p => p.type === selectedPlan);
  const mainMonthly = planObj ? planObj.monthly : 0;
  const mainAnnual = planObj ? planObj.annual : 0;

  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedAgeBand, setSelectedAgeBand] = useState(null);

  const scrollToSection = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan.type);
    setProgressStep(2);
    scrollToSection(tierSectionRef);
  };

  const handleTierSelect = (tier) => {
    setSelectedTier(tier);
    scrollToSection(ageSectionRef); // Auto-scrolls to age selection after selecting a tier
  };

  const handleAgeSelect = (idx) => {
    setSelectedAgeBand(idx);
    scrollToSection(relationshipSectionRef);
  };



  const getTierData = (tier) => tier === 'tier1' ? tier1Data : tier2Data;
  const tierMonthly = selectedTier && selectedAgeBand !== null ? getTierData(selectedTier)[selectedAgeBand].monthly : 0;
  const tierAnnual = selectedTier && selectedAgeBand !== null ? getTierData(selectedTier)[selectedAgeBand].annual : 0;

  const totalMonthly = mainMonthly + tierMonthly;
  const totalAnnual = mainAnnual + tierAnnual;

  const canConfirm = selectedPlan && selectedTier && selectedAgeBand !== null && selectedRelationship;

  let tierLabel = '';
  let tierNameForURL = '';
  if (selectedTier === 'tier1') {
    tierLabel = 'Tier 1';
    tierNameForURL = 'tier1';
  } else if (selectedTier === 'tier2') {
    tierLabel = 'Tier 2';
    tierNameForURL = 'tier2';
  }

  let ageGroupLabel = '';
  if (selectedTier && selectedAgeBand !== null) {
    ageGroupLabel = getTierData(selectedTier)[selectedAgeBand].ageBandLabel;
  }

  useEffect(() => {
    const updateSummaryPosition = () => {
      if (!beneficiaryRef.current || !summaryRightRef.current) return;
  
      const navHeight = 120; // fixed top offset for navigation
      const gap = 32; // horizontal gap
      const parentRect = beneficiaryRef.current.getBoundingClientRect();
      
      // If parent is below nav, position at top of parent
      if (parentRect.top > navHeight) {
        summaryRightRef.current.style.position = "absolute";
        summaryRightRef.current.style.top = "0";
        summaryRightRef.current.style.bottom = "auto";
      } 
      // Otherwise, keep fixed at navHeight
      else {
        summaryRightRef.current.style.position = "fixed";
        summaryRightRef.current.style.top = `${navHeight}px`;
        summaryRightRef.current.style.bottom = "auto";
      }
      
      // Handle horizontal positioning
      if (window.innerWidth > 1024) {
        const beneficiaryLeftElem = beneficiaryRef.current.querySelector('.beneficiary-left');
        if (beneficiaryLeftElem) {
          if (summaryRightRef.current.style.position === "fixed") {
            const leftRect = beneficiaryLeftElem.getBoundingClientRect();
            summaryRightRef.current.style.left = `${leftRect.right + gap}px`;
          } else {
            // When absolute, position relative to parent
            summaryRightRef.current.style.left = "auto";
            summaryRightRef.current.style.right = "15";
          }
        }
      }
    };
  
    window.addEventListener("scroll", updateSummaryPosition);
    window.addEventListener("resize", updateSummaryPosition);
    updateSummaryPosition();
    
    return () => {
      window.removeEventListener("scroll", updateSummaryPosition);
      window.removeEventListener("resize", updateSummaryPosition);
    };
  }, []);

  return (
    <div style={{ overflowX: 'hidden' }}>
      <Header />
      <div className="progress-bar-container" style={{ top: '71px' }}>
        <div className="progress-steps">
          <span className={`progress-step ${progressStep >= 1 ? 'active' : ''}`}>1. Your Plan</span>
          <span className={`progress-step ${progressStep >= 2 ? 'active' : ''}`}>2. Added Life Details</span>
        </div>
        <div className="progress-line">
          <div className="progress-line-fill" style={{ width: `${progressStep === 1 ? '50%' : '100%'}` }}></div>
        </div>
      </div>

      <div className="cover-options-page">
        <div className="cover-options-container" ref={coverOptionsRef}>
          <h1 className="page-title">Your insurance plan</h1>
          <p className="page-subtitle">
            Choose the perfect plan to secure your future.
          </p>

          <div className="pricing-grid-container">
            <div className="pricing-grid">
              {mainLifeCover.map((plan, index) => (
                <div className="pricing-card" key={index}>
                  <div className="card-content">
                    <h3 className="plan-type">{plan.type}</h3>
                    <ul className="features-list">
                      {plan.features.map((feature, i) => (
                        <li key={i}>
                          <span className="feature-title">{feature.title}</span>
                          <span className="feature-value">{feature.value}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePlanSelect(plan)}
                      className="select-button plan-btn-top"
                    >
                      Select Plan
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {selectedPlan && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div className="down-arrow" onClick={() => tierSectionRef.current.scrollIntoView({ behavior: 'smooth' })}>
              ↓
            </div>
          </div>
        )}

        <div ref={tierSectionRef}></div>
        {selectedPlan && (
          <div className="beneficiary-section" ref={beneficiaryRef}>
            <div className="beneficiary-left">
              <h1 className="section-heading">1. Select your Added life's coverage level</h1>
              <div
                className={`option-card ${selectedTier === 'tier1' ? 'selected' : ''}`}
                onClick={() => handleTierSelect('tier1')}
              >
                <div className="option-card-inner">
                  <input type="radio" checked={selectedTier === 'tier1'} readOnly />
                  <div className="option-card-content">
                    <h3>Tier 1</h3>
                    <p>Base coverage with Life Cover of Gh₵ 1,000</p>
                  </div>
                </div>
              </div>
              <div
                className={`option-card ${selectedTier === 'tier2' ? 'selected' : ''}`}
                onClick={() => handleTierSelect('tier2')}
              >
                <div className="option-card-inner">
                  <input type="radio" checked={selectedTier === 'tier2'} readOnly />
                  <div className="option-card-content">
                    <h3>Tier 2</h3>
                    <p>Enhanced coverage with Life Cover of Gh₵ 2,000</p>
                  </div>
                </div>
              </div>

              <div ref={ageSectionRef}></div>
              {selectedTier && (
                <>
                  <h1 className="section-heading" style={{ marginTop: '2rem' }}>2. Select your Added life's age group</h1>
                  {getTierData(selectedTier).map((band, idx) => (
                    <div className={`option-card ${selectedAgeBand === idx ? 'selected' : ''}`} key={idx} onClick={() => handleAgeSelect(idx)}>
                      <div className="option-card-inner">
                        <input type="radio" checked={selectedAgeBand === idx} readOnly />
                        <div className="option-card-content">
                          <h4>{band.ageBandLabel}</h4>
                          <p>Monthly Premium: GH₵ {band.monthly.toFixed(2)} | Annual Premium: GH₵ {band.annual.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              <div ref={relationshipSectionRef}></div>
              {selectedAgeBand !== null && (
                <div className="relationship-selection">
                  <h1 className="section-heading" style={{ marginTop: '2rem' }}>3. Select Relationship with Added life</h1>
                  {relationships.map((rel, index) => (
                    <div
                      key={index}
                      className={`option-card ${selectedRelationship === rel ? 'selected' : ''}`}
                      onClick={() => setSelectedRelationship(rel)}
                    >
                      <div className="option-card-inner">
                        <input type="radio" checked={selectedRelationship === rel} readOnly />
                        <div className="option-card-content">
                          <h4>{rel}</h4>
                          <p>Maximum: {relationshipLimits[rel]}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="summary-right" ref={summaryRightRef}>
              <div className="summary-box plan-summary-box">
                <h2 className="summary-title">Your Plan Summary</h2>

                <div className="summary-row">
                  <span className="label">Selected Plan:</span>
                  <span className="value"><strong>{selectedPlan || 'N/A'}</strong></span>
                </div>
                <div className="summary-row">
                  <span className="label">Cover Tier:</span>
                  <span className="value"><strong>{tierLabel || 'N/A'}</strong></span>
                </div>
                <div className="summary-row">
                  <span className="label">Relationship:</span>
                  <span className="value"><strong>{selectedRelationship || 'N/A'}</strong></span>
                </div>
                <div className="summary-row">
                  <span className="label">Age Group:</span>
                  <span className="value"><strong>{ageGroupLabel || 'N/A'}</strong></span>
                </div>
                <div className="summary-row">
                  <span className="label">Monthly Premium:</span>
                  <span className="value"><strong>GH₵ {totalMonthly.toFixed(2)}</strong></span>
                </div>
                <div className="summary-row">
                  <span className="label">Annual Premium:</span>
                  <span className="value"><strong>GH₵ {totalAnnual.toFixed(2)}</strong></span>
                </div>

                <Link
                  to={canConfirm
                    ? `/purchase?plan=${selectedPlan.toLowerCase()}&tier=${tierNameForURL}&ageBandIndex=${selectedAgeBand}&relationship=${selectedRelationship}&monthly=${totalMonthly.toFixed(2)}&annual=${totalAnnual.toFixed(2)}`
                    : "#"
                  }
                  className={`select-button continue-btn ${!canConfirm ? 'disabled' : ''}`}
                  style={{
                    marginTop: '1rem',
                    display: 'inline-block',
                    pointerEvents: canConfirm ? 'auto' : 'none',
                    opacity: canConfirm ? 1 : 0.5
                  }}
                >
                  {canConfirm ? "Purchase Policy" : "Complete Selections to Continue"}
                </Link>

              </div>
            </div>
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
};

export default CoverOptions;
